import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
    Users,
    Key,
    ArrowLeft,
    Shield,
    UserCog,
    Loader2,
    Eye,
    EyeOff,
} from 'lucide-react';

export default function GestionUsuariosPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [usuarios, setUsuarios] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);

    // Change own password dialog
    const [ownPasswordDialog, setOwnPasswordDialog] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [ownNewPassword, setOwnNewPassword] = useState('');
    const [ownConfirmPassword, setOwnConfirmPassword] = useState('');

    useEffect(() => {
        loadUsuarios();
    }, []);

    const loadUsuarios = async () => {
        setLoading(true);
        try {
            const res = await authAPI.getUsuarios();
            setUsuarios(res.data);
        } catch (err) {
            console.error('Error loading usuarios:', err);
            toast.error('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenChangePassword = (usuario) => {
        setSelectedUser(usuario);
        setNewPassword('');
        setConfirmPassword('');
        setShowPassword(false);
        setDialogOpen(true);
    };

    const handleChangePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        setSaving(true);
        try {
            await authAPI.changePassword(selectedUser.id, newPassword);
            toast.success(`Contraseña de ${selectedUser.nombre} actualizada`);
            setDialogOpen(false);
            setSelectedUser(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al cambiar contraseña');
        } finally {
            setSaving(false);
        }
    };

    const handleChangeOwnPassword = async () => {
        if (!ownNewPassword || ownNewPassword.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }
        if (ownNewPassword !== ownConfirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        setSaving(true);
        try {
            await authAPI.changeOwnPassword(currentPassword, ownNewPassword);
            toast.success('Tu contraseña ha sido actualizada');
            setOwnPasswordDialog(false);
            setCurrentPassword('');
            setOwnNewPassword('');
            setOwnConfirmPassword('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al cambiar contraseña');
        } finally {
            setSaving(false);
        }
    };

    const getRolBadgeColor = (rol) => {
        switch (rol) {
            case 'superadmin': return 'bg-red-500 text-white';
            case 'admin': return 'bg-orange-500 text-white';
            case 'gerente': return 'bg-blue-500 text-white';
            case 'seguridad': return 'bg-green-500 text-white';
            case 'solicitante': return 'bg-gray-500 text-white';
            default: return 'bg-gray-500 text-white';
        }
    };

    const canChangePassword = (targetUser) => {
        if (user.rol === 'superadmin') return true;
        if (user.rol === 'admin') {
            // Admin can't change superadmin password
            if (targetUser.rol === 'superadmin') return false;
            // Admin can only change passwords of users in same company
            return targetUser.empresa_id === user.empresa_id;
        }
        return false;
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/home')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <UserCog className="h-6 w-6" />
                            Gestión de Usuarios
                        </h1>
                        <p className="text-muted-foreground">Administrar usuarios y contraseñas</p>
                    </div>
                </div>
                <Button variant="outline" onClick={() => setOwnPasswordDialog(true)} data-testid="change-own-password-btn">
                    <Key className="h-4 w-4 mr-2" />
                    Cambiar Mi Contraseña
                </Button>
            </div>

            {/* Users Table */}
            <Card className="border-2">
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Usuarios del Sistema
                    </CardTitle>
                    <CardDescription>
                        {user.rol === 'superadmin' 
                            ? 'Todos los usuarios del sistema' 
                            : 'Usuarios de tu empresa'}
                    </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nombre</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Rol</TableHead>
                                <TableHead>Empresa</TableHead>
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {usuarios.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                        No hay usuarios registrados
                                    </TableCell>
                                </TableRow>
                            ) : (
                                usuarios.map((u) => (
                                    <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                                                    {u.nombre?.charAt(0) || 'U'}
                                                </div>
                                                <span className="font-medium">{u.nombre}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-mono text-sm">{u.email}</TableCell>
                                        <TableCell>
                                            <Badge className={`${getRolBadgeColor(u.rol)} capitalize`}>
                                                {u.rol === 'superadmin' && <Shield className="h-3 w-3 mr-1" />}
                                                {u.rol}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {u.empresa_id ? 'Empresa Demo' : 'Sistema'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {canChangePassword(u) && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleOpenChangePassword(u)}
                                                    data-testid={`change-password-${u.email}`}
                                                >
                                                    <Key className="h-4 w-4 mr-1" />
                                                    Cambiar Clave
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Change Password Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cambiar Contraseña</DialogTitle>
                        <DialogDescription>
                            Ingresa la nueva contraseña para {selectedUser?.nombre}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="new-password">Nueva Contraseña</Label>
                            <div className="relative">
                                <Input
                                    id="new-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Mínimo 6 caracteres"
                                    data-testid="new-password-input"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirm-password">Confirmar Contraseña</Label>
                            <Input
                                id="confirm-password"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repite la contraseña"
                                data-testid="confirm-password-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleChangePassword} disabled={saving} data-testid="save-password-btn">
                            {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Key className="h-4 w-4 mr-2" />
                            )}
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Change Own Password Dialog */}
            <Dialog open={ownPasswordDialog} onOpenChange={setOwnPasswordDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cambiar Mi Contraseña</DialogTitle>
                        <DialogDescription>
                            Ingresa tu contraseña actual y la nueva contraseña
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="current-password">Contraseña Actual</Label>
                            <Input
                                id="current-password"
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="Tu contraseña actual"
                                data-testid="current-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="own-new-password">Nueva Contraseña</Label>
                            <Input
                                id="own-new-password"
                                type="password"
                                value={ownNewPassword}
                                onChange={(e) => setOwnNewPassword(e.target.value)}
                                placeholder="Mínimo 6 caracteres"
                                data-testid="own-new-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="own-confirm-password">Confirmar Nueva Contraseña</Label>
                            <Input
                                id="own-confirm-password"
                                type="password"
                                value={ownConfirmPassword}
                                onChange={(e) => setOwnConfirmPassword(e.target.value)}
                                placeholder="Repite la nueva contraseña"
                                data-testid="own-confirm-password-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOwnPasswordDialog(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleChangeOwnPassword} disabled={saving} data-testid="save-own-password-btn">
                            {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Key className="h-4 w-4 mr-2" />
                            )}
                            Cambiar Contraseña
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
